import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ABTest } from '@freelancer/abtest';
import { AuthServiceInterface } from '@freelancer/auth';
import { Localization } from '@freelancer/localization';
import { Location } from '@freelancer/location';
import { LoginSignupService } from '@freelancer/login-signup';
import { RedirectToPhp } from '@freelancer/redirect-to-php';
import { Tracking } from '@freelancer/tracking';
import * as Rx from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { LOGIN_AUTH_SERVICE } from './login.config';
import { LoginServiceInterface } from './login.interface';

/**
 * This service can be injected as a login service.
 * It redirects to the gaf/login.php page unless `w=t` is set.
 * In future this may just become a `NoRedirectLogin` service.
 */
@Injectable({
  providedIn: 'root',
})
export class Login implements LoginServiceInterface {
  constructor(
    @Inject(LOGIN_AUTH_SERVICE) private auth: AuthServiceInterface,
    @Inject(PLATFORM_ID) private platformId: Object,
    private abtest: ABTest,
    private localization: Localization,
    private location: Location,
    private loginSignupService: LoginSignupService,
    private redirectToPhp: RedirectToPhp,
    private tracking: Tracking,
  ) {}

  redirect(
    currentUrl: string,
    routeSnapshot: ActivatedRouteSnapshot,
  ): Rx.Observable<boolean> {
    return this.auth.isLoggedIn().pipe(
      switchMap(loggedIn => {
        if (loggedIn) {
          return this.loggedInRedirect(routeSnapshot);
        }

        return this.loggedOutRedirect(currentUrl, routeSnapshot);
      }),
    );
  }

  private loggedInRedirect(
    routeSnapshot: ActivatedRouteSnapshot,
  ): Rx.Observable<boolean> {
    this.loginSignupService
      .getRedirectUrl('login', {
        redirectDisabled: false,
        queryParams: routeSnapshot.queryParams,
      })
      .then(redirectUrl => this.location.navigateByUrl(redirectUrl || '/'));
    return Rx.of(false);
  }

  private loggedOutRedirect(
    currentUrl: string,
    routeSnapshot?: ActivatedRouteSnapshot,
  ): Rx.Observable<boolean> {
    if (routeSnapshot) {
      if (routeSnapshot.queryParams.w === 't') {
        return Rx.of(true);
      }
      if (routeSnapshot.queryParams.w === 'f') {
        return this.loginRedirectToPhp(currentUrl);
      }
    }

    if (isPlatformServer(this.platformId)) {
      return Rx.from(
        this.abtest
          .handleServer()
          .then(() =>
            this.redirectToPhp
              .doRedirect(decodeURIComponent(currentUrl))
              .toPromise(),
          ),
      );
    }

    if (this.abtest.isWhitelistUser()) {
      return Rx.of(true);
    }

    return this.tracking.getSessionId().pipe(
      switchMap(sessionId => {
        if (
          // English user only.
          this.localization.isEnglish() &&
          this.abtest.shouldEnrol(
            'T140960-webapp-login-signup-v3',
            sessionId,
            1.0,
          )
        ) {
          // https://www.freelancer.com/admin/abtest/index.php?action=show_experiment&experiment_id=1070
          return this.abtest
            .getSessionExperimentVariation('T140960-webapp-login-signup-v3')
            .pipe(
              switchMap(variation => {
                if (variation === 'test') {
                  return Rx.of(true);
                }
                return this.loginRedirectToPhp(currentUrl);
              }),
            );
        }

        return this.loginRedirectToPhp(currentUrl);
      }),
    );
  }

  private loginRedirectToPhp(currentUrl: string) {
    return this.redirectToPhp.doRedirect(decodeURIComponent(currentUrl));
  }
}
